/**
 * Read-only traversal of the layout tree: section → field collection → (band →) column → row →
 * field. Kept free of runtime imports so anything that renders or inspects a layout can use it
 * without pulling in i18n, Vue or the template-form domain.
 *
 * A field collection carries its columns in one of two shapes: directly, or grouped into bands.
 * Both are live — the bands backfill (2605281200_migrate_templates_to_layout_bands) covered only
 * the documents/projects/assets modules and only `fields` blocks, so contacts, files, emails,
 * accounting and every notes/folders/bookings block still store columns directly. The two are
 * mutually exclusive in the data; where both appear, bands win, which is what gets rendered.
 *
 * Shapes are structural, not the generated API types, because the template editor works on
 * partially built form objects: a section under construction may not carry columns or rows yet.
 */

import type { LayoutBandSettings, LayoutBandType } from "@scayla/api-client-fetch";

/**
 * Normalizes a field collection to the band list to render. Returns the collection's own `bands`
 * array by identity when it has one, so a render pass sees a stable reference.
 */
export function fieldCollectionBands<C>(fieldColl: {
  columns: C[];
  bands?: FieldCollectionBand<C>[];
}): FieldCollectionBand<C>[] {
  if (fieldColl.bands?.length) return fieldColl.bands;
  return [{ columns: fieldColl.columns }];
}

/** A band as rendered; a band without `type` lays out fields. */
export interface FieldCollectionBand<C> {
  id?: string;
  columns: C[];
  type?: LayoutBandType;
  settings?: LayoutBandSettings;
}

interface FieldCarryingColumn<F> {
  rows?: { fields?: F[] }[];
}
interface FieldCarryingCollection<F> {
  columns?: FieldCarryingColumn<F>[];
  bands?: { columns?: FieldCarryingColumn<F>[] }[];
}
/** Exported so a caller holding an SFC-derived section type can cast to it at the call site. */
export interface FieldCarryingSection<F> {
  fieldCollections?: FieldCarryingCollection<F>[];
}

/**
 * Collects all field proxies from a sections structure, in layout order. Applies the same
 * bands-win rule as fieldCollectionBands, but flattens past the band grouping and tolerates every
 * level being absent, so the two cannot share one signature.
 */
export function collectSectionFields<F>(sections: FieldCarryingSection<F>[]): F[] {
  return sections
    .flatMap((s) => s.fieldCollections ?? [])
    .flatMap((fc) => (fc.bands?.length ? fc.bands.flatMap((b) => b.columns ?? []) : (fc.columns ?? [])))
    .flatMap((c) => c.rows ?? [])
    .flatMap((r) => r.fields ?? []);
}
